/**
 * A note with shape, and the plain text that has to stay true to it.
 *
 * The document is the thing a teacher edits. The text is what everything else reads —
 * the conflict screen that asks her to choose between two versions, the Learning
 * Companion that builds her credit evidence, search. Those must never disagree with what
 * she sees, so the text is derived here and never written by hand.
 *
 * Deliberately a small vocabulary rather than "whatever the editor emits". A document is
 * stored for years and rendered into a PDF, a screen, and plain text; every node type in
 * it is a thing three renderers have to agree about. Anything unrecognised is dropped on
 * the way in rather than stored and puzzled over later.
 */

export type NoteMark = "bold" | "italic" | "underline" | "strike" | "code" | "highlight";

export interface NoteText {
  type: "text";
  text: string;
  marks?: readonly NoteMark[];
  /** Present on a link. Checked on the way in; never rendered as given. */
  href?: string;
}

export type NoteBlock =
  | { type: "paragraph"; content: readonly NoteText[] }
  | { type: "heading"; level: 1 | 2 | 3; content: readonly NoteText[] }
  | { type: "quote"; content: readonly NoteText[] }
  | { type: "codeBlock"; text: string }
  | { type: "bulletList"; items: readonly NoteListItem[] }
  | { type: "orderedList"; items: readonly NoteListItem[] }
  | { type: "taskList"; items: readonly NoteTaskItem[] }
  | { type: "divider" };

export interface NoteListItem {
  content: readonly NoteText[];
  /** Nesting, which is what indent and outdent produce. */
  children?: readonly NoteListItem[];
}

export interface NoteTaskItem {
  content: readonly NoteText[];
  done: boolean;
}

export interface NoteDocument {
  type: "doc";
  content: readonly NoteBlock[];
}

export const EMPTY_NOTE_DOCUMENT: NoteDocument = { type: "doc", content: [] };

/** Deep enough for a real outline, shallow enough that a PDF can render it. */
export const MAX_LIST_DEPTH = 4;
const MARKS: readonly NoteMark[] = ["bold", "italic", "underline", "strike", "code", "highlight"];

/**
 * The plain text of a document, and the single source of `body`.
 *
 * Headings and paragraphs become lines. List items keep their bullet or number, because
 * a teacher who wrote a list and later reads her evidence should still see a list. Task
 * items keep their box, ticked or not, because whether she did the thing is the point of
 * writing it down.
 */
export function noteDocumentText(document: NoteDocument): string {
  return document.content.map(blockText).filter((line) => line !== null).join("\n\n").trim();
}

function blockText(block: NoteBlock): string | null {
  if (block.type === "divider") return null;
  if (block.type === "codeBlock") return block.text;
  if (block.type === "bulletList") return listText(block.items, 0, () => "•");
  if (block.type === "orderedList") return listText(block.items, 0, (index) => `${index + 1}.`);
  if (block.type === "taskList") {
    return block.items.map((item) => `${item.done ? "[x]" : "[ ]"} ${inlineText(item.content)}`).join("\n");
  }
  const text = inlineText(block.content);
  return text ? text : null;
}

function listText(
  items: readonly NoteListItem[],
  depth: number,
  bullet: (index: number) => string,
): string {
  const indent = "  ".repeat(depth);
  return items.flatMap((item, index) => {
    const line = `${indent}${bullet(index)} ${inlineText(item.content)}`;
    if (!item.children?.length || depth + 1 >= MAX_LIST_DEPTH) return [line];
    return [line, listText(item.children, depth + 1, bullet)];
  }).join("\n");
}

function inlineText(content: readonly NoteText[]): string {
  return content.map((run) => run.text).join("").trim();
}

/**
 * A document from a browser, made safe.
 *
 * Everything unrecognised is dropped rather than refused, because a note is a person's
 * own work and losing all of it over one bad node would be worse than losing the node.
 * Nothing here throws.
 */
export function sanitizeNoteDocument(value: unknown): NoteDocument {
  if (!isObject(value) || value.type !== "doc" || !Array.isArray(value.content)) {
    return EMPTY_NOTE_DOCUMENT;
  }
  const content = value.content.map(sanitizeBlock).filter((block): block is NoteBlock => block !== null);
  return { type: "doc", content };
}

function sanitizeBlock(value: unknown): NoteBlock | null {
  if (!isObject(value)) return null;
  switch (value.type) {
    case "paragraph":
      return { type: "paragraph", content: sanitizeInline(value.content) };
    case "heading": {
      const level = value.level === 1 || value.level === 2 || value.level === 3 ? value.level : 2;
      return { type: "heading", level, content: sanitizeInline(value.content) };
    }
    case "quote":
      return { type: "quote", content: sanitizeInline(value.content) };
    case "codeBlock":
      return { type: "codeBlock", text: typeof value.text === "string" ? value.text : "" };
    case "bulletList":
      return { type: "bulletList", items: sanitizeItems(value.items, 0) };
    case "orderedList":
      return { type: "orderedList", items: sanitizeItems(value.items, 0) };
    case "taskList":
      return {
        type: "taskList",
        items: (Array.isArray(value.items) ? value.items : [])
          .filter(isObject)
          .map((item) => ({ content: sanitizeInline(item.content), done: item.done === true })),
      };
    case "divider":
      return { type: "divider" };
    default:
      return null;
  }
}

function sanitizeItems(value: unknown, depth: number): NoteListItem[] {
  if (!Array.isArray(value) || depth >= MAX_LIST_DEPTH) return [];
  return value.filter(isObject).map((item) => {
    const children = sanitizeItems(item.children, depth + 1);
    return children.length
      ? { content: sanitizeInline(item.content), children }
      : { content: sanitizeInline(item.content) };
  });
}

function sanitizeInline(value: unknown): NoteText[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isObject).flatMap((run) => {
    if (typeof run.text !== "string" || !run.text) return [];
    const marks = Array.isArray(run.marks)
      ? run.marks.filter((mark): mark is NoteMark => MARKS.includes(mark as NoteMark))
      : [];
    const href = safeHref(run.href);
    const text: NoteText = { type: "text", text: run.text };
    return [{
      ...text,
      ...(marks.length ? { marks } : {}),
      ...(href ? { href } : {}),
    }];
  });
}

/**
 * A link a teacher typed, or one somebody hoped she would paste.
 *
 * Only ordinary web and mail addresses survive. `javascript:` is the obvious one, but
 * anything with a scheme we did not name is dropped, because this text is rendered back
 * to her and eventually into a document she sends somebody else.
 */
function safeHref(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    return ["http:", "https:", "mailto:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

/** A note written before documents existed, read for the first time. */
export function noteDocumentFromText(body: string): NoteDocument {
  const paragraphs = body.split(/\n{2,}/u).map((part) => part.trim()).filter(Boolean);
  return {
    type: "doc",
    content: paragraphs.map((text) => ({ type: "paragraph", content: [{ type: "text", text }] })),
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
